
import * as _ from "lodash";

import { updateParameter } from "./UpdateParameter";

import { printWarning, ThunkResult } from "../../shared/utilities";

import { ParameterEditorMode } from "../components/ParameterInput";
import { IParameterSchema } from "../model/IProcessData";
import { ParameterEditorValue } from "../model/ParameterEditorValue";

import { State } from "../model/State";
import { changeFromExpressionToArrayType } from "./UpdateParameter";
import { HashMap } from "../../shared/model/HashMap";

export function changeParameterInputMode(elementId: string | undefined, key: string | undefined, parameterSchema: IParameterSchema | undefined | null, current: ParameterEditorValue, newModeName: string)
    : ThunkResult<void, State> {
    return (dispatch) => {
        let newMode = (ParameterEditorMode as unknown as HashMap<ParameterEditorMode>)[newModeName] as ParameterEditorMode;
        if (!newMode) {
            printWarning(`Invalid new mode name: ${newModeName}. Will default to 'csharp'`);
            newMode = ParameterEditorMode.csharp;
        }

        if (newMode === ParameterEditorMode.array) {
            dispatch(changeFromExpressionToArrayType(elementId, key));
            return;
        }

        if (!current) {
            printWarning(`No current value: ${current}. Will assume 'text' with empty string`);
            current = { mode: ParameterEditorMode[ParameterEditorMode.handlebars], value: "" };
        }

        let currentMode = (ParameterEditorMode as unknown as HashMap<ParameterEditorMode>)[current.mode];
        if (!currentMode) {
            printWarning(`Invalid current mode: ${current.mode}. Will assume 'text'`);
            currentMode = ParameterEditorMode.handlebars;
        }

        let currentValue = current.value;
        if (_.isNil(currentValue)) {
            printWarning(`Invalid current value: ${current.value}. Will use empty string instead`);
            currentValue = "";
        }

        const newParameterValue = getValueOnModeChange(newMode, currentMode, currentValue, parameterSchema);
        dispatch(updateParameter(elementId, key, {
            mode: ParameterEditorMode[newMode],
            value: newParameterValue
        }));
    };
}

function getValueOnModeChange(newMode: ParameterEditorMode, currentMode: ParameterEditorMode, currentValue: string | number | boolean, parameterSchema: IParameterSchema | undefined | null) {
    // if not changing from expression mode, no need to change value
    if (currentMode !== ParameterEditorMode.csharp) {
        return currentValue;
    }

    if (!parameterSchema) {
        printWarning(`No parameter schema defined, cannot get default value. Will just use the current value, hopefully it works: ${currentValue}`);
        return currentValue;
    }

    if (newMode === ParameterEditorMode.select) {
        // always clear the current selection

        // use the given default if set
        if (parameterSchema.defaultValue) {
            return parameterSchema.defaultValue;
        }

        if (!parameterSchema.subParameters || !_.isArray(parameterSchema.subParameters)) {
            printWarning(`Invalid enum details in parameter schema: ${parameterSchema.subParameters}. Will default to empty string as enum value.`);
            return "";
        }

        const firstEnum = parameterSchema.subParameters[0];
        const firstEnumName = firstEnum && firstEnum.name;
        if (!firstEnumName) {
            printWarning("Empty select value in schema, will default to empty string");
            return "";
        }
        return firstEnumName;

    } else if (newMode === ParameterEditorMode.integer) {
        let intValue = parseInt(currentValue.toString(), 10);
        if (isNaN(intValue)) {
            // not a number -> try default instead
            intValue = parseInt(parameterSchema.defaultValue, 10);
            if (isNaN(intValue)) {
                printWarning(`Invalid default value for integer: '${parameterSchema.defaultValue}'. Will default to 0`);
                return 0;
            }
        }

        return intValue;

    } else if (newMode === ParameterEditorMode.toggle) {
        if (_.isBoolean(currentValue)) {
            return currentValue;
        }
        return currentValue === "true" || !!parameterSchema.defaultValue;
    }

    // probably text, just return it
    return currentValue;
}
